import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft,
  Mic,
  Volume2,
  Play,
  Upload,
  FileText,
  Loader2,
  Radio,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Sparkles,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Pause,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// WebRTC Audio Chat class
class RealtimeAudioChat {
  constructor(onMessage, onConnectionChange) {
    this.peerConnection = null;
    this.dataChannel = null;
    this.audioElement = null;
    this.mediaStream = null;
    this.onMessage = onMessage;
    this.onConnectionChange = onConnectionChange;
  }

  async init() {
    try {
      this.onConnectionChange?.("connecting");
      
      const tokenRes = await fetch(`${BACKEND_URL}/api/realtime/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });
      
      const tokenData = await tokenRes.json();
      
      if (!tokenRes.ok) {
        throw new Error(tokenData.detail || "Failed to get session token");
      }
      
      const ephemeralToken = tokenData.value;
      
      if (!ephemeralToken) {
        throw new Error("No ephemeral token received from server");
      }
      
      this.peerConnection = new RTCPeerConnection();
      
      this.audioElement = document.createElement("audio");
      this.audioElement.autoplay = true;
      this.peerConnection.ontrack = (e) => {
        this.audioElement.srcObject = e.streams[0];
      };

      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.peerConnection.addTrack(this.mediaStream.getTracks()[0]);
      } catch (micErr) {
        throw new Error("Microphone access denied. Please allow microphone access and try again.");
      }

      this.dataChannel = this.peerConnection.createDataChannel("oai-events");
      this.dataChannel.addEventListener("message", (e) => {
        try {
          if (typeof e.data === 'string' && e.data.startsWith('{')) {
            const msg = JSON.parse(e.data);
            this.onMessage?.(msg);
          }
        } catch (err) {
          // Ignore
        }
      });

      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);

      const sdpRes = await fetch("https://api.openai.com/v1/realtime/calls", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${ephemeralToken}`,
          "Content-Type": "application/sdp"
        },
        body: offer.sdp
      });

      if (!sdpRes.ok) {
        throw new Error(`OpenAI connection failed (${sdpRes.status})`);
      }

      const answerSdp = await sdpRes.text();
      await this.peerConnection.setRemoteDescription({
        type: "answer",
        sdp: answerSdp
      });

      this.onConnectionChange?.("connected");
    } catch (err) {
      console.error("Init error:", err);
      this.onConnectionChange?.("disconnected");
      throw err;
    }
  }

  sendMessage(msg) {
    if (this.dataChannel?.readyState === "open") {
      this.dataChannel.send(JSON.stringify(msg));
    }
  }
  
  // Force stop AI from speaking - clear everything
  interruptAI() {
    // Cancel any in-progress response
    this.sendMessage({ type: "response.cancel" });
    // Clear input audio buffer
    this.sendMessage({ type: "input_audio_buffer.clear" });
  }
  
  // Mute/unmute audio output
  muteAudio(mute) {
    if (this.audioElement) {
      this.audioElement.muted = mute;
    }
  }
  
  // Pause/resume microphone
  setMicEnabled(enabled) {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => {
        track.enabled = enabled;
      });
    }
  }

  disconnect() {
    this.mediaStream?.getTracks().forEach(track => track.stop());
    this.peerConnection?.close();
    this.dataChannel?.close();
    this.onConnectionChange?.("disconnected");
  }
}

export default function DeckReviewPage() {
  const navigate = useNavigate();
  
  // Connection state
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [audioChat, setAudioChat] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  
  // Deck state
  const [slideImages, setSlideImages] = useState([]);
  const [slideTexts, setSlideTexts] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [uploadingDeck, setUploadingDeck] = useState(false);
  const [deckFileName, setDeckFileName] = useState("");
  
  // Review state
  const [reviewStarted, setReviewStarted] = useState(false);
  const [currentlyReviewingSlide, setCurrentlyReviewingSlide] = useState(-1);
  
  // Summary report state
  const [summaryReport, setSummaryReport] = useState(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [transcriptBuffer, setTranscriptBuffer] = useState("");
  
  const fileInputRef = useRef(null);
  const audioChatRef = useRef(null);
  const isRequestingReviewRef = useRef(false);

  useEffect(() => {
    audioChatRef.current = audioChat;
  }, [audioChat]);

  // Handle page visibility - pause when user leaves, resume when returns
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!audioChatRef.current) return;
      
      if (document.hidden) {
        // User left the page - mute audio and pause mic
        audioChatRef.current.muteAudio(true);
        audioChatRef.current.setMicEnabled(false);
        setIsPaused(true);
      } else {
        // User returned - unmute
        audioChatRef.current.muteAudio(false);
        audioChatRef.current.setMicEnabled(true);
        setIsPaused(false);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    
    // Also handle when window loses focus (e.g., switching apps)
    const handleBlur = () => {
      if (audioChatRef.current) {
        audioChatRef.current.muteAudio(true);
        audioChatRef.current.setMicEnabled(false);
        setIsPaused(true);
      }
    };
    
    const handleFocus = () => {
      if (audioChatRef.current && connectionStatus === "connected") {
        audioChatRef.current.muteAudio(false);
        audioChatRef.current.setMicEnabled(true);
        setIsPaused(false);
      }
    };
    
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
    };
  }, [connectionStatus]);

  const handleMessage = useCallback((msg) => {
    if (msg.type === "input_audio_buffer.speech_started") {
      setIsListening(true);
    } else if (msg.type === "input_audio_buffer.speech_stopped") {
      setIsListening(false);
    } else if (msg.type === "response.audio.delta") {
      setIsSpeaking(true);
    } else if (msg.type === "response.audio.done" || msg.type === "response.done") {
      setIsSpeaking(false);
      isRequestingReviewRef.current = false;
    } else if (msg.type === "response.cancelled") {
      setIsSpeaking(false);
      isRequestingReviewRef.current = false;
    } else if (msg.type === "response.audio_transcript.done") {
      if (msg.transcript) {
        setTranscriptBuffer(prev => prev + "\n" + msg.transcript);
      }
    }
  }, []);

  const handleConnectionChange = useCallback((status) => {
    setConnectionStatus(status);
  }, []);

  const resetDeckState = () => {
    setSlideImages([]);
    setSlideTexts([]);
    setCurrentSlide(0);
    setDeckFileName("");
    setReviewStarted(false);
    setCurrentlyReviewingSlide(-1);
    setSummaryReport(null);
    setTranscriptBuffer("");
    isRequestingReviewRef.current = false;
  };

  const startSession = async () => {
    try {
      resetDeckState();
      
      const chat = new RealtimeAudioChat(handleMessage, handleConnectionChange);
      await chat.init();
      
      if (chat.dataChannel) {
        const waitForChannel = () => new Promise(resolve => {
          if (chat.dataChannel.readyState === "open") resolve();
          else chat.dataChannel.addEventListener("open", resolve, { once: true });
        });
        await waitForChannel();
        
        const systemPrompt = `You are hosting "AI-Investor Office Hours" - an experienced VC partner giving founders honest, unfiltered feedback on their pitch decks.

REVIEW STRUCTURE (follow this exact flow):
1. FIRST IMPRESSION: Start with your immediate gut reaction as an investor seeing this slide for the first time. Be honest:
   - "My first thought looking at this is..." 
   - "Honestly, I'm not sure what this slide is trying to say..."
   - "This immediately grabs me because..."
   - "I'm a bit lost here - there's a lot going on..."
   - "OK so this is clearly about X, that's good..."
2. WHAT WORKS: If something works, say it briefly
3. ONE FIX: Give ONE specific, actionable improvement

CRITICAL RULES:
- Keep responses SHORT (20-25 seconds max)
- Be HONEST - investors are scanning decks quickly. If it's confusing, say so.
- Be warm but direct - sugarcoating doesn't help founders
- Common first impressions: "too much text", "what's the key message?", "I don't get it", "this is clear", "strong visual"
- If interrupted, immediately stop and await new instructions
- NEVER continue speaking about a previous slide if asked about a new one

NEVER BREAK CHARACTER.`;

        chat.sendMessage({
          type: "session.update",
          session: {
            instructions: systemPrompt,
            voice: "verse",
            modalities: ["text", "audio"],
            input_audio_transcription: { model: "whisper-1" },
            turn_detection: null // Disable auto turn detection for more control
          }
        });
        
        setTimeout(() => {
          chat.sendMessage({
            type: "conversation.item.create",
            item: {
              type: "message",
              role: "user",
              content: [{ type: "input_text", text: "Welcome me briefly to AI-Investor Office Hours and ask me to upload my pitch deck. Keep it to 1 sentence." }]
            }
          });
          chat.sendMessage({ type: "response.create" });
        }, 1500);
      }
      
      setAudioChat(chat);
    } catch (err) {
      console.error("Session error:", err);
      toast.error(err.message || "Failed to start session");
    }
  };

  const endSession = () => {
    audioChat?.disconnect();
    setAudioChat(null);
    setIsSpeaking(false);
    setIsListening(false);
    setIsPaused(false);
    resetDeckState();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.match(/\.(pdf|pptx?)$/i)) {
      toast.error("Please upload PDF or PowerPoint");
      return;
    }
    
    setUploadingDeck(true);
    setDeckFileName(file.name);
    setSlideImages([]);
    setSlideTexts([]);
    setCurrentSlide(0);
    setReviewStarted(false);
    setCurrentlyReviewingSlide(-1);
    setSummaryReport(null);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await fetch(`${BACKEND_URL}/api/deck-review/upload`, {
        method: "POST",
        body: formData
      });
      
      if (!res.ok) throw new Error("Failed to process deck");
      
      const data = await res.json();
      setSlideImages(data.slide_images || []);
      setSlideTexts(data.slide_texts || []);
      
      const numSlides = data.slide_images?.length || 0;
      
      if (audioChatRef.current && connectionStatus === "connected") {
        audioChatRef.current.interruptAI();
        setTimeout(() => {
          audioChatRef.current.sendMessage({
            type: "conversation.item.create",
            item: {
              type: "message",
              role: "user",
              content: [{ 
                type: "input_text", 
                text: `Deck uploaded: "${file.name}" with ${numSlides} slides. Acknowledge briefly and say "Click on slide 1 to start the review."`
              }]
            }
          });
          audioChatRef.current.sendMessage({ type: "response.create" });
        }, 200);
      }
      
      toast.success(`${numSlides} slides loaded`);
    } catch (err) {
      toast.error("Failed to process deck");
    } finally {
      setUploadingDeck(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const reviewSlide = useCallback((slideIndex) => {
    if (!audioChatRef.current || connectionStatus !== "connected") {
      toast.error("Not connected");
      return;
    }
    if (slideIndex < 0 || slideIndex >= slideImages.length) return;
    
    // IMMEDIATELY interrupt any ongoing AI speech
    audioChatRef.current.interruptAI();
    setIsSpeaking(false);
    
    // Update UI immediately
    setCurrentSlide(slideIndex);
    setCurrentlyReviewingSlide(slideIndex);
    setReviewStarted(true);
    isRequestingReviewRef.current = true;
    
    const slideText = slideTexts[slideIndex] || "No text visible";
    
    // Send new review request after brief delay to ensure interrupt processed
    setTimeout(() => {
      if (!audioChatRef.current) return;
      
      audioChatRef.current.sendMessage({
        type: "conversation.item.create",
        item: {
          type: "message",
          role: "user",
          content: [{ 
            type: "input_text", 
            text: `[SLIDE ${slideIndex + 1} OF ${slideImages.length}]
            
Content: "${slideText.substring(0, 600)}"

Give your HONEST first impression as an investor seeing this slide:
- What's your gut reaction? (confused? clear? too busy? compelling?)
- What works (if anything)?
- One specific fix

Be direct and under 25 seconds.`
          }]
        }
      });
      audioChatRef.current.sendMessage({ type: "response.create" });
    }, 250);
  }, [connectionStatus, slideImages.length, slideTexts]);

  const nextSlide = () => {
    if (currentSlide < slideImages.length - 1) {
      reviewSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      reviewSlide(currentSlide - 1);
    }
  };

  const askForSummary = async () => {
    if (!audioChatRef.current || connectionStatus !== "connected") return;
    
    setGeneratingSummary(true);
    audioChatRef.current.interruptAI();
    
    audioChatRef.current.sendMessage({
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [{ 
          type: "input_text", 
          text: `Give a 20-second FINAL SUMMARY: Top 3 priorities to improve this deck. Be specific.`
        }]
      }
    });
    audioChatRef.current.sendMessage({ type: "response.create" });
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/deck-review/generate-summary`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filename: deckFileName,
          slide_count: slideImages.length,
          slide_texts: slideTexts,
          conversation_notes: transcriptBuffer
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setSummaryReport(data.report);
      }
    } catch (err) {
      console.error("Summary error:", err);
    } finally {
      setGeneratingSummary(false);
    }
  };

  const hasDeck = slideImages.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800/50 bg-slate-950/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-emerald-400" />
            <span className="font-semibold text-sm">AI-Investor Office Hours</span>
          </div>
          <Badge className={`text-xs ${connectionStatus === "connected" ? (isPaused ? "bg-yellow-500/20 text-yellow-400" : "bg-green-500/20 text-green-400") : "bg-slate-700 text-slate-400"}`}>
            <Radio className={`w-2 h-2 mr-1 ${connectionStatus === "connected" && !isPaused ? "animate-pulse" : ""}`} />
            {connectionStatus === "connected" ? (isPaused ? "Paused" : "Live") : "Offline"}
          </Badge>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4">
        {/* Not Connected */}
        {connectionStatus !== "connected" ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-4xl mb-6 shadow-xl">
              📊
            </div>
            <h1 className="text-2xl font-bold mb-3">AI-Investor Office Hours</h1>
            <p className="text-slate-400 mb-6 max-w-md mx-auto text-sm">
              Get live voice feedback on your pitch deck, slide by slide.
            </p>
            <Button 
              onClick={startSession} 
              disabled={connectionStatus === "connecting"}
              className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-5"
            >
              {connectionStatus === "connecting" ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connecting...</>
              ) : (
                <><Play className="w-4 h-4 mr-2" /> Start Office Hours</>
              )}
            </Button>
          </div>
        ) : (
          /* Connected */
          <div className="space-y-4">
            {/* Paused overlay */}
            {isPaused && (
              <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg px-4 py-2 flex items-center gap-2 text-yellow-400 text-sm">
                <Pause className="w-4 h-4" />
                Session paused - return to this tab to resume
              </div>
            )}
            
            {/* AI Status */}
            <div className="flex items-center justify-between bg-slate-800/50 rounded-lg px-4 py-2">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-xl ${isSpeaking ? 'animate-pulse' : ''}`}>
                  🎯
                </div>
                <div>
                  <p className="font-medium text-sm">Office Hours Host</p>
                  <p className="text-xs text-slate-400">
                    {isSpeaking ? "Speaking..." : isListening ? "Listening..." : "Ready"}
                  </p>
                </div>
              </div>
              {(isSpeaking || isListening) && !isPaused && (
                <div className="flex gap-1">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className={`w-1.5 h-4 rounded-full ${isSpeaking ? 'bg-purple-400' : 'bg-cyan-400'} animate-pulse`} style={{ animationDelay: `${i * 0.15}s` }} />
                  ))}
                </div>
              )}
            </div>

            {/* Slide Viewer */}
            <Card className="bg-slate-900/50 border-slate-800 overflow-hidden">
              <CardContent className="p-0">
                {!hasDeck ? (
                  <div className="aspect-video flex items-center justify-center bg-slate-800/50 p-8">
                    {uploadingDeck ? (
                      <div className="text-center">
                        <Loader2 className="w-10 h-10 mx-auto animate-spin text-emerald-400 mb-3" />
                        <p className="text-slate-400 text-sm">Processing slides...</p>
                      </div>
                    ) : (
                      <div className="text-center">
                        <Upload className="w-12 h-12 mx-auto text-slate-600 mb-3" />
                        <Button onClick={() => fileInputRef.current?.click()} className="bg-emerald-600 hover:bg-emerald-700">
                          <Upload className="w-4 h-4 mr-2" /> Upload Deck
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <div className="aspect-video bg-black flex items-center justify-center relative">
                      <img 
                        src={slideImages[currentSlide]} 
                        alt={`Slide ${currentSlide + 1}`}
                        className="max-w-full max-h-full object-contain"
                      />
                      <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded text-xs">
                        {currentSlide + 1} / {slideImages.length}
                      </div>
                      {currentlyReviewingSlide === currentSlide && isSpeaking && (
                        <div className="absolute top-2 left-2 bg-emerald-500/90 px-2 py-1 rounded text-xs flex items-center gap-1">
                          <Volume2 className="w-3 h-3 animate-pulse" /> Reviewing
                        </div>
                      )}
                    </div>
                    
                    <div className="p-3 bg-slate-800/50 flex items-center justify-between">
                      <Button variant="outline" size="sm" onClick={prevSlide} disabled={currentSlide === 0} className="border-slate-600">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-xs text-slate-400 truncate max-w-[150px]">{deckFileName}</span>
                      <Button variant="outline" size="sm" onClick={nextSlide} disabled={currentSlide === slideImages.length - 1} className="border-slate-600">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Thumbnails */}
            {hasDeck && (
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
                {slideImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => reviewSlide(idx)}
                    className={`flex-shrink-0 w-16 h-10 rounded border-2 overflow-hidden transition-all ${
                      idx === currentSlide ? 'border-emerald-500 ring-2 ring-emerald-500/30' : 'border-slate-700 hover:border-slate-500'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Actions */}
            {hasDeck && (
              <div className="grid grid-cols-2 gap-2">
                {!reviewStarted ? (
                  <Button onClick={() => reviewSlide(0)} className="col-span-2 bg-gradient-to-r from-emerald-500 to-teal-500">
                    <Play className="w-4 h-4 mr-2" /> Start Review
                  </Button>
                ) : (
                  <>
                    <Button onClick={() => reviewSlide(currentSlide)} variant="outline" className="border-emerald-600 text-emerald-400">
                      <MessageSquare className="w-4 h-4 mr-1" /> Re-review
                    </Button>
                    <Button onClick={askForSummary} disabled={generatingSummary} className="bg-purple-600 hover:bg-purple-700">
                      {generatingSummary ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                      Summary
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Bottom buttons */}
            <div className="flex gap-2">
              <Button onClick={() => fileInputRef.current?.click()} variant="outline" size="sm" disabled={uploadingDeck} className="flex-1 border-slate-700 text-slate-400">
                <RefreshCw className="w-3 h-3 mr-1" /> New Deck
              </Button>
              <Button onClick={endSession} variant="outline" size="sm" className="flex-1 border-red-500/50 text-red-400">
                End Session
              </Button>
            </div>

            {/* Summary Report */}
            {summaryReport && (
              <Card className="bg-slate-800/50 border-emerald-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <h3 className="font-bold text-emerald-400">Office Hours Summary</h3>
                  </div>
                  
                  {summaryReport.priorities && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-sm mb-2">Top Priorities</h4>
                      <ol className="space-y-2">
                        {summaryReport.priorities.map((item, idx) => (
                          <li key={idx} className="flex gap-2 text-sm">
                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-xs font-bold">{idx + 1}</span>
                            <span className="text-slate-300">{item}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                  
                  {summaryReport.strengths && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-sm mb-2">Strengths</h4>
                      <ul className="space-y-1">
                        {summaryReport.strengths.map((item, idx) => (
                          <li key={idx} className="text-sm text-slate-300 flex gap-2">
                            <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />{item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {summaryReport.improvements && (
                    <div>
                      <h4 className="font-semibold text-sm mb-2">To Improve</h4>
                      <ul className="space-y-1">
                        {summaryReport.improvements.map((item, idx) => (
                          <li key={idx} className="text-sm text-slate-300 flex gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />{item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>
      
      <input ref={fileInputRef} type="file" accept=".pdf,.ppt,.pptx" onChange={handleFileUpload} className="hidden" />
    </div>
  );
}
